/**
 * The six landing routes.
 *
 * Prerendered to static HTML: the content is in the source of the page, readable by a crawler, an
 * LLM fetcher, or curl. The home page's counters are the one live read, and they degrade to a
 * stated absence rather than to zeros — see `Counters`.
 */
import { useEffect, useState } from "react"
import { registry, type StateOfRisk } from "@agitome/ui-shared"
import { preloaded } from "@agitome/ui-shared"
import { Shell } from "@agitome/ui-shared"
import { Capture } from "@agitome/ui-shared"

/**
 * The four hero numbers, read live.
 *
 * The original design labelled these "sample data" because they were invented. They are real now, so
 * the label goes — but the failure mode replaces it rather than being dropped: if the API cannot
 * be reached this renders "not loaded" and says so, because four zeros on a risk registry is a
 * claim that nothing has ever been reported.
 */
function Counters() {
  // Prerendered with real figures, then refreshed. The hero of the whole site cannot be four
  // dashes in the document a crawler reads.
  const [data, setData] = useState<StateOfRisk | null>(preloaded<StateOfRisk>("drop"))
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    registry.stateOfRisk().then(setData).catch(() => setFailed(data === null))
  }, [])

  // AN EMPTY CORPUS IS NOT A STATISTIC, and rendering it as one is the loudest thing on the
  // site. `0 reported · 0 adjudicated · 0 subjects · 0%` above the fold does not read as "early",
  // it reads as abandoned — and it is the first thing every visitor sees.
  //
  // This is NOT hiding the number. The registry is one click away and says the same thing in a
  // place where it means what it says; /principles is built on refusing to publish a figure that
  // implies more than it knows, and four zeros in a hero implies a measurement was taken.
  //
  // The band comes back on its own the moment there is one row. Nothing to remember, nothing to
  // switch off, no chance of it staying suppressed after it stops being true.
  const empty = data !== null && data.incidents.reported === 0 && data.incidents.verified === 0

  const cells: [string, string][] = data
    ? [
        [data.incidents.reported.toLocaleString("en-US"), "Reported incidents"],
        [data.incidents.verified.toLocaleString("en-US"), "Adjudicated"],
        [
          (data.by_framework.length + data.by_model.length + data.by_category.length).toLocaleString("en-US"),
          "Subjects tracked",
        ],
        [`${Math.round(data.resolution_rate * 100)}%`, "Marked resolved"],
      ]
    : [
        ["—", "Reported incidents"],
        ["—", "Adjudicated"],
        ["—", "Subjects tracked"],
        ["—", "Marked resolved"],
      ]

  if (empty) {
    return (
      <div className="band band--acid band--grain band--tight">
        <div className="wrap">
          <p className="fine" style={{ opacity: 0.9 }}>
            <strong>Nothing has been reported yet.</strong> This registry publishes what strangers
            file and what adjudication confirms — it prints no number it has not earned, so there
            is no number here. <a href="/tools/incidents">File the first one</a>, or{" "}
            <a href="/principles">read what we will not do</a> with it.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="band band--acid band--grain band--tight">
      <div className="wrap">
        <div className="grid grid-4">
          {cells.map(([n, l]) => (
            <div className="stat" key={l}>
              <div className="stat__n">{n}</div>
              <div className="stat__l">{l}</div>
            </div>
          ))}
        </div>
        {failed ? (
          <p className="fine mt-5" style={{ opacity: 0.8 }}>
            These could not be loaded just now. That is a failure to reach agitome, not a count —
            an empty registry and an unreachable one look identical in a number, so this says which.
          </p>
        ) : (
          <p className="fine mt-5" style={{ opacity: 0.8 }}>
            Live counts from the public registry. Reported and adjudicated are shown separately and
            never summed — most of this corpus is unverified signal, and the split is the point.
          </p>
        )}
      </div>
    </div>
  )
}

const TOOLS: [string, string, string, string][] = [
  ["/tools/incidents", "01", "Search the incident database", "Every reported agent failure, by framework, model, category, vendor or DID. Each has its own page and its own link."],
  ["/tools/risk-profiles", "02", "Read a risk profile", "“Is LangChain risky to deploy?” — the counts, the failure modes, the tier, and every rule that produced it."],
  ["/tools/risk-report", "03", "Pull a risk report", "One report across your whole stack. Shareable, and a snapshot: the link shows what you saw."],
  ["/tools/gate", "04", "Run the admission gate", "Your policy over reported data. Allow, review or deny — before the thing reaches production."],
  ["/tools/exposure", "05", "Estimate your exposure", "What an agent could cost you if it fails, and an honest answer on whether anybody could underwrite it yet."],
  ["/tools/report", "06", "File an incident", "Two fields, under a minute, no account. It enters as an unverified signal and is queued for adjudication."],
  ["/tools/categories", "07", "Browse by failure mode", "The closed taxonomy every product in the portfolio classifies against — so counts are comparable across subjects."],
  ["/tools/state-of-agent-risk", "08", "Read the State of Agent Risk", "The periodic drop off the whole corpus, with the methodology printed beside it so it can be quoted in full."],
  ["/tools/track-record", "09", "Verify a track record", "Check a signed track-record export offline — without trusting agitome to tell you the truth about it."],
  ["/tools/vendors", "10", "Dispute or claim a subject", "This page is about your product. Four things you can do, and three of them need no account at all."],
  ["/tools/overlay", "11", "Embed the risk badge", "A flat, cacheable badge for your own directory or docs. An image — no runtime of ours on your page."],
  ["/tools/oracle", "12", "Read about attestations", "ERC-8004 attestations, for agents whose standing has to be readable by a contract rather than a person."],
]

export function Home() {
  return (
    <Shell>
      <div className="band band--ink band--mesh band--grain">
        <div className="wrap">
          <p className="live">Free · no account · every claim answerable by the party it names</p>
          <h1 className="mega mt-4 balance" style={{ maxWidth: "16ch" }}>
            What is this agent's <span className="hot">risk</span>?
          </h1>
          <p className="lede mt-6 measure">
            Before you ship on a framework, a model, or somebody else's agent — see what has
            actually gone wrong with it. Free, no account, and every claim is answerable by the
            party it names.
          </p>
          <div className="row mt-6">
            <a className="btn btn--primary btn--lg" href="/tools/risk-report">Pull a risk report</a>
            <a className="btn btn--ghost btn--lg" href="/tools/incidents">Browse the registry</a>
          </div>
        </div>
      </div>

      <Counters />

      <div className="band band--paper band--tight">
        <div className="wrap">
          <p className="eyebrow">Built on</p>
          <div className="standards mt-4">
            <span>Ed25519</span><span>did:key</span><span>JCS canonicalisation</span>
            <span>ERC-8004</span><span>OpenTelemetry GenAI</span><span>A closed failure taxonomy</span>
          </div>
          <p className="fine mt-5" style={{ maxWidth: "62ch" }}>
            Every tier prints the rules that fired and the numbers they read, so you can recompute
            it from the page rather than trust it — and every export is signed over its canonical
            form, so you can check a track record offline without trusting agitome to tell you the
            truth about it.
          </p>
        </div>
      </div>

      <div className="band band--bone">
        <div className="wrap">
          <p className="eyebrow">What agitome will not do</p>
          <p className="serif mt-5" style={{ fontSize: "var(--t-display)", maxWidth: "20ch" }}>
            A report is a signal. It is <em>not</em> a finding, and we will not print it as one.
          </p>
          <p className="lede mt-6 measure">
            Most of this registry is what strangers have reported. Some has been adjudicated
            against a signed loss event. Those are different claims about the world, and every page
            says which one you are looking at.
          </p>
          <div className="grid grid-2 mt-8">
            <div className="ticket ticket--reported">
              <span className="badge badge--reported">Reported</span>
              <p className="mt-3">Filed by the public. Published, searchable, and it moves no agent's score.</p>
            </div>
            <div className="ticket ticket--verified">
              <span className="badge badge--verified">Verified</span>
              <p className="mt-3">Adjudicated, with a signed loss event behind it that agitome resolved and confirmed.</p>
            </div>
          </div>
          <div className="grid grid-3 mt-8">
            <div><h3 className="h3">No rankings.</h3><p className="fine mt-3">Two models half a point apart, over populations of different sizes, are not first and second at anything.</p></div>
            <div><h3 className="h3">No incident rate.</h3><p className="fine mt-3">No denominator exists for how many deployments there are, so there is no rate to publish.</p></div>
            <div><h3 className="h3">No prices.</h3><p className="fine mt-3">The calculator does arithmetic on your numbers. It is not a quote, and will not be until the loss data exists.</p></div>
          </div>
        </div>
      </div>

      <div className="band band--paper">
        <div className="wrap">
          <p className="eyebrow">What you can do with it</p>
          <h2 className="display mt-4 balance" style={{ maxWidth: "14ch" }}>Twelve things, all free, no account.</h2>
          <div className="grid grid-4 mt-8">
            {TOOLS.map(([href, n, title, blurb]) => (
              <a className="card tool" href={href} key={href}>
                <p className="tool__n">{n}</p>
                <h3 className="h3 mt-3">{title}</h3>
                <p className="fine mt-3">{blurb}</p>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="band band--violet band--grain">
        <div className="wrap">
          <div className="grid grid-split">
            <div>
              <p className="eyebrow eyebrow--accent">If this is about your product</p>
              <h2 className="display mt-4 balance">Anyone can dispute an incident. You can do more than that.</h2>
              <p className="lede mt-6">
                Claim the subject, and your dispute takes an incident out of every published
                statistic while contested — it stays visible, because hiding a contested claim is
                what makes a registry unauditable. If we decide against you, you can still attach a
                permanent statement.
              </p>
              <p className="mt-6"><a className="btn btn--primary" href="/tools/vendors">How to claim your subject</a></p>
            </div>
            <div className="slab">
              <p className="eyebrow">And if we miss a deadline</p>
              <p className="mt-4">
                Disputes are adjudicated in 30 days, 45 if we record an extension. Past that, the
                dispute resolves <strong>in your favour automatically</strong> and the incident
                stops counting.
              </p>
              <p className="fine mt-4" style={{ color: "inherit", opacity: 0.75 }}>
                The party that failed to decide in time is agitome, and the cost of that lands here
                rather than on you.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="band band--ink band--mesh">
        <div className="wrap center">
          <p className="eyebrow eyebrow--accent">The monthly drop</p>
          <h2 className="display mt-4 balance" style={{ marginInline: "auto", maxWidth: "18ch" }}>
            What broke, what got fixed, and what the data <span className="hot">cannot</span> yet say.
          </h2>
          <div className="capture mt-6" style={{ maxWidth: "30rem", marginInline: "auto" }}>
            <Capture source="newsletter" cta="Subscribe" />
          </div>
        </div>
      </div>
    </Shell>
  )
}

export function How() {
  return (
    <Shell>
      <div className="band band--ink band--mesh">
        <div className="wrap">
          <p className="eyebrow eyebrow--accent">How it works</p>
          <h1 className="display mt-4 balance" style={{ maxWidth: "18ch" }}>
            Two corpora. One of them is <span className="hot">evidence</span>.
          </h1>
          <p className="lede mt-6 measure">
            Nearly every mistake people make reading agitome comes from collapsing these two into
            one thing. They are separate on purpose, and the product never mixes them.
          </p>
        </div>
      </div>
      <div className="band band--bone">
        <div className="wrap">
          <div className="grid grid-2">
            <div className="card">
              <span className="badge badge--reported">The registry</span>
              <h2 className="h2 mt-4">What strangers reported</h2>
              <dl className="spec mt-5">
                <div><dt>Source</dt><dd>Incidents anyone files about named products</dd></div>
                <div><dt>Standing</dt><dd>Reported. Unverified.</dd></div>
                <div><dt>Scope</dt><dd>Public, global, no account</dd></div>
                <div><dt>Pipeline</dt><dd>incidents → profiles → risk-data → reports</dd></div>
              </dl>
            </div>
            <div className="card">
              <span className="badge badge--verified">The record</span>
              <h2 className="h2 mt-4">What a tenant signed</h2>
              <dl className="spec mt-5">
                <div><dt>Source</dt><dd>Signed loss events a tenant emits</dd></div>
                <div><dt>Standing</dt><dd>Verified, attributable</dd></div>
                <div><dt>Scope</dt><dd>Per-tenant, private</dd></div>
                <div><dt>Pipeline</dt><dd>evidence → score → vet → cover</dd></div>
              </dl>
            </div>
          </div>
          <p className="serif mt-8" style={{ fontSize: "var(--t-h2)", maxWidth: "30ch" }}>
            A report only becomes evidence by being <em>adjudicated</em> against a signed loss
            event. Nothing else promotes it.
          </p>
        </div>
      </div>
      <div className="band band--paper">
        <div className="wrap">
          <p className="eyebrow">The path a report takes</p>
          <div className="grid grid-4 mt-6">
            {[
              ["1", "Filed", "Two fields. It publishes immediately, marked reported, and moves nobody's score."],
              ["2", "Contested", "The named party can claim the subject and dispute. While contested it leaves every statistic."],
              ["3", "Adjudicated", "A human decides, against signed evidence, inside a clock that runs in your favour."],
              ["4", "Counted", "Only now does it carry weight — and the reasoning is reproducible from the page."],
            ].map(([n, title, body]) => (
              <div key={n}>
                <p className="stat__n" style={{ fontSize: "clamp(2rem,4vw,3rem)" }}>{n}</p>
                <h3 className="h3 mt-2">{title}</h3>
                <p className="fine mt-2">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Shell>
  )
}

export function Principles() {
  const refusals: [string, string][] = [
    ["No rankings", "Two models half a point apart, over populations of different sizes, are not first and second at anything. We publish tiers, never leaderboards."],
    ["No incident rate", "A rate needs a denominator — how many deployments exist — and nobody reports one. A number derived without it would be a guess wearing a decimal point."],
    ["No prices", "The exposure calculator does arithmetic on numbers you supply. It is not a quote and will not become one until the loss data exists to price against."],
    ["No hidden claims", "A contested incident stays visible while it is contested. Hiding it would make the registry unauditable, which is worth more than the complaint it would settle."],
    ["No silent scoring", "Every tier prints the rules that fired and the numbers they read, so you can recompute it from the page rather than trust it."],
    ["No reported-as-verified", "The single distinction the whole product rests on. Amber is a signal. Teal has a signed loss event behind it. They never blur."],
  ]
  return (
    <Shell>
      <div className="band band--acid band--grain">
        <div className="wrap">
          <p className="eyebrow">The constraints</p>
          <h1 className="mega mt-4 balance" style={{ maxWidth: "12ch" }}>
            What we <em style={{ fontFamily: "var(--serif)" }}>won't</em> do.
          </h1>
          <p className="lede mt-6 measure">
            A registry that publishes other people's failures earns nothing but the trust it
            refuses to spend. These are the refusals, and they are load-bearing.
          </p>
        </div>
      </div>
      <div className="band band--paper">
        <div className="wrap">
          <dl className="spec">
            {refusals.map(([term, detail]) => (
              <div key={term}><dt>{term}</dt><dd>{detail}</dd></div>
            ))}
          </dl>
        </div>
      </div>
    </Shell>
  )
}

export function Pricing() {
  return (
    <Shell>
      <div className="band band--ink band--mesh band--grain">
        <div className="wrap">
          <p className="eyebrow eyebrow--accent">Pricing</p>
          <h1 className="display mt-4 balance" style={{ maxWidth: "20ch" }}>
            Looking is free forever. <span className="hot">Enforcing</span> is the product.
          </h1>
          <p className="lede mt-6 measure">
            Every one of the twelve tools stays free and needs no account — charging a reader to
            check whether an agent is safe would kill the only loop this registry has. What costs
            money is what a free lookup structurally cannot be: continuous, enforced in your
            pipeline, and answerable to a deadline.
          </p>
        </div>
      </div>
      <div className="band band--paper">
        <div className="wrap">
          <div className="grid grid-3">
            {[
              ["Free · forever · no account", "Look it up", "All twelve tools. Incidents, risk profiles, failure modes, the admission gate, risk reports, exposure, the drop, track-record verification.",
                [["Reads", "Everything published"], ["Rate", "Fair-use, unauthenticated"], ["Costs", "Nothing, and it never will"]], "/tools", "Open the tools", "btn--ghost"],
              ["Design partner", "Gate it", "The admission gate as an API your CI actually calls, over your policy, across your whole stack — plus alerts when a subject you depend on moves tier.",
                [["Adds", "Gate API, CI check"], ["Watch", "Your subjects, continuously"], ["Reports", "Scheduled, shareable, snapshotted"]], "/partners", "Apply →", "btn--primary"],
              ["Design partner", "Underwrite it", "For carriers and self-insured operators: adjudicated loss events, cross-org reconciliation, and the evidence trail behind every tier we publish.",
                [["Adds", "Adjudication SLA, dispute queue"], ["Evidence", "Signed loss events, exportable"], ["Standing", "Portable, verifiable off-platform"]], "/partners", "Talk to us →", "btn--primary"],
            ].map(([eyebrow, title, body, specs, href, cta, cls]) => (
              <div className="card" key={title as string}>
                <p className="eyebrow">{eyebrow as string}</p>
                <h2 className="h2 mt-3">{title as string}</h2>
                <p className="mt-4">{body as string}</p>
                <dl className="spec mt-5">
                  {(specs as [string, string][]).map(([k, v]) => (
                    <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
                  ))}
                </dl>
                <p className="mt-5">
                  <a className={`btn ${cls as string} btn--block`} href={href as string}>{cta as string}</a>
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="band band--bone">
        <div className="wrap">
          <p className="eyebrow">Why there is no number on this page</p>
          <p className="serif mt-5" style={{ fontSize: "var(--t-h2)", maxWidth: "32ch" }}>
            A price implies we know what a failure costs. We do not have the loss data yet, and
            printing one anyway would be the first thing we had to retract.
          </p>
          <p className="lede mt-6 measure">
            <a href="/principles">What we won't do</a> commits to no prices until the loss data
            exists to price against. That constraint applies to our own pricing page, or it was
            never a constraint.
          </p>
        </div>
      </div>
    </Shell>
  )
}

export function Partners() {
  return (
    <Shell>
      <div className="band band--violet band--grain">
        <div className="wrap">
          <p className="eyebrow eyebrow--accent">Founding partners · ten places</p>
          <h1 className="display mt-4 balance" style={{ maxWidth: "22ch" }}>
            We would rather have ten operators who <span className="hot">argue</span> with us.
          </h1>
          <p className="lede mt-6 measure">
            The registry works. What it has not had is ten teams running it against a real stack and
            telling us where the taxonomy is wrong. Ten places, because ten is what we can support
            properly — this is a capacity limit, not a marketing device.
          </p>
        </div>
      </div>
      <div className="band band--paper">
        <div className="wrap">
          <div className="grid grid-2">
            <div className="card">
              <p className="eyebrow">What you get</p>
              <h2 className="h2 mt-3">All of it, free, for the term</h2>
              <dl className="spec mt-5">
                <div><dt>Gate</dt><dd>The admission gate in your CI, over your policy</dd></div>
                <div><dt>Watch</dt><dd>Alerts when a subject you depend on moves</dd></div>
                <div><dt>Access</dt><dd>A direct line to the people building it</dd></div>
                <div><dt>Input</dt><dd>Roadmap and taxonomy — the closed category list is the part we most want argued with</dd></div>
                <div><dt>Price</dt><dd>Fixed in writing at the end of the term, before you decide</dd></div>
              </dl>
            </div>
            <div className="card">
              <p className="eyebrow">What we ask</p>
              <h2 className="h2 mt-3">Two things, and neither is a logo</h2>
              <dl className="spec mt-5">
                <div><dt>Time</dt><dd>An hour a fortnight, with somebody who runs the stack</dd></div>
                <div><dt>Candour</dt><dd>Tell us when the tier is wrong. A partner who agrees with everything is not testing anything</dd></div>
              </dl>
              <p className="fine mt-5">
                A reference or a logo is something you may offer later if the product earns it. It is
                not part of the exchange, and we will not print your name until you have told us in
                writing that we may.
              </p>
            </div>
          </div>
          <div className="capture mt-8" style={{ maxWidth: "34rem" }}>
            <p className="eyebrow">Apply</p>
            <p className="mt-3">
              One field. We reply either way, and we say no more often than yes — a partner whose
              stack we cannot help is a waste of both our fortnights.
            </p>
            <Capture source="partner_application" cta="Apply" />
          </div>
        </div>
      </div>
      <div className="band band--ink">
        <div className="wrap center">
          <p className="eyebrow eyebrow--accent">Nothing here is behind this form</p>
          <h2 className="display mt-4 balance" style={{ marginInline: "auto", maxWidth: "22ch" }}>
            Every tool stays free whether you apply or not.
          </h2>
          <p className="mt-6"><a className="btn btn--ghost btn--lg" href="/tools">Go and use them</a></p>
        </div>
      </div>
    </Shell>
  )
}
